/**
 * 완료 알림 — "플레이리스트를 저장했습니다" 같은 한 줄이 화면 아래에 잠깐 떴다 사라진다.
 *
 * 저장·삭제·배정 뒤에 창이 닫히고 목록만 슬쩍 바뀌면, 눌렀는데 된 것인지 다시 열어 확인하게 된다 —
 * 특히 결과가 목록에 바로 드러나지 않는 일(편성 저장, 비밀번호 재설정, 캐시 지우기 지시)이 그렇다.
 * 실패는 여기서 알리지 않는다 — 실패는 사라지면 안 되므로 그 자리의 오류 줄(`ErrorNote`)이 맡는다.
 *
 * 부르는 쪽은 `notify('…')` 한 줄이거나, `useAction().run(일, 다음, '…')` 의 세 번째 값이다.
 */
import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { Check } from 'lucide-react';

interface Toast {
  id: number;
  message: string;
}

/** 떠 있는 시간. 한 줄을 읽기에 넉넉하고, 다음 일을 가리지 않을 만큼. */
export const TOAST_MS = 3500;
/** 한꺼번에 보이는 수 — 일괄 작업이 줄줄이 알려도 화면을 덮지 않게. */
const MAX_VISIBLE = 3;

let toasts: readonly Toast[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function notify(message: string): void {
  toasts = [...toasts, { id: nextId++, message }].slice(-MAX_VISIBLE);
  emit();
}

function dismiss(id: number): void {
  toasts = toasts.filter((toast) => toast.id !== id);
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function ToastItem({ toast }: { toast: Toast }): ReactNode {
  useEffect(() => {
    const timer = window.setTimeout(() => dismiss(toast.id), TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [toast.id]);
  return (
    <div className="toast">
      <Check aria-hidden="true" className="toast-icon" strokeWidth={2.5} />
      <span>{toast.message}</span>
    </div>
  );
}

/** 앱에 한 번 둔다. 화면 낭독기는 떠오른 문장을 읽는다(`aria-live`). */
export function ToastHost(): ReactNode {
  const items = useSyncExternalStore(subscribe, () => toasts);
  return (
    <div className="toast-host" role="status" aria-live="polite">
      {items.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
